import React from 'react';
import type { PresentationAuditEntry, PresentationAuditLinkKind } from '../../services/programPresentationAuditService';
import {
  presentationRefSlideLinkStateLabel,
  type PresentationRefSlideLinkState,
} from '../../utils/programPresentationLinkState';

function linkKindClass(kind: PresentationAuditLinkKind): string {
  if (kind === 'orphan_unreferenced_ref' || kind === 'orphan_owner_missing') {
    return 'cp-presentation-audit-row--orphan';
  }
  return '';
}

function linkStateClass(state: PresentationRefSlideLinkState | null | undefined): string {
  if (state === 'ref_no_slides' || state === 'slides_no_ref') {
    return 'cp-presentation-audit-row--link-warning';
  }
  return '';
}

function ownerKindLabel(kind: PresentationAuditEntry['ownerKind']): string {
  switch (kind) {
    case 'program_cell':
      return 'program_cell';
    case 'scoped_row':
      return 'scoped_row';
    case 'fixed_row':
      return 'fixed_row';
    case 'presentation_ref':
      return 'presentation_ref';
    default:
      return kind;
  }
}

export type PresentationAuditSectionProps = {
  entries: PresentationAuditEntry[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  onSelectSectionKeys: (keys: string[], checked: boolean) => void;
  emptyMessage?: string;
};

const PresentationAuditSection: React.FC<PresentationAuditSectionProps> = ({
  entries,
  selected,
  onToggle,
  onSelectSectionKeys,
  emptyMessage = '프레젠테이션 슬라이드 데이터가 없습니다.',
}) => {
  const keys = entries.map((e) => e.selectionKey);
  const orphanCount = entries.filter(
    (e) => e.linkKind === 'orphan_unreferenced_ref' || e.linkKind === 'orphan_owner_missing'
  ).length;
  const refAndSlidesCount = entries.filter((e) => e.linkState === 'ref_and_slides').length;
  const refNoSlidesCount = entries.filter((e) => e.linkState === 'ref_no_slides').length;
  const slidesNoRefCount = entries.filter((e) => e.linkState === 'slides_no_ref').length;
  const hiddenCount = entries.filter((e) => e.linkKind === 'linked_hidden_legacy_cell').length;

  return (
    <section className="cp-program-data-cleanup-section">
      <div className="cp-program-data-cleanup-section-head">
        <h4>프레젠테이션 슬라이드 — program_content_slide_pages ({entries.length}건)</h4>
        {keys.length ? (
          <button
            type="button"
            className="cp-btn-secondary cp-program-data-cleanup-select-all"
            onClick={() => onSelectSectionKeys(keys, keys.some((k) => !selected.has(k)))}
          >
            {keys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
          </button>
        ) : null}
      </div>
      <p className="page-desc cp-program-data-cleanup-section-desc">
        owner별 슬라이드 캔버스(<code>program_content_slide_pages</code>)와 학습내용 ref 연결 상태입니다.{' '}
        <strong>ref + 슬라이드</strong> {refAndSlidesCount}건 · <strong>ref만</strong> {refNoSlidesCount}건 ·{' '}
        <strong>슬라이드만</strong> {slidesNoRefCount}건
        {orphanCount > 0 ? (
          <>
            {' '}
            · <span className="cp-presentation-audit-orphan-note">고아 {orphanCount}건</span>
          </>
        ) : null}
        {hiddenCount > 0 ? (
          <>
            {' '}
            · <span className="cp-presentation-audit-orphan-note">UI 미노출 연결 {hiddenCount}건</span>
          </>
        ) : null}
        . 슬라이드 삭제는 페이지만 지우며, <code>empty-ref:</code> 항목은 ref만 제거합니다.
      </p>
      {entries.length ? (
        <div className="cp-program-data-cleanup-table-wrap">
          <table className="cp-program-data-cleanup-table cp-program-data-cleanup-table--db cp-presentation-audit-table">
            <thead>
              <tr>
                <th className="cp-program-data-cleanup-col-check" scope="col" aria-label="선택" />
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>owner_kind</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>owner_id</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>pages</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>page_index</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity">
                  <code>context</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>learning_body</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>link_state</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>link_status</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>updated_at</code>
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.selectionKey}
                  className={[linkKindClass(entry.linkKind), linkStateClass(entry.linkState)].filter(Boolean).join(' ')}
                >
                  <td className="cp-program-data-cleanup-col-check">
                    <input
                      type="checkbox"
                      checked={selected.has(entry.selectionKey)}
                      onChange={() => onToggle(entry.selectionKey)}
                      aria-label={`${entry.linkLabel} ${entry.ownerKind} ${entry.ownerId}`}
                    />
                  </td>
                  <td className="cp-program-data-cleanup-col-db">
                    <code>{ownerKindLabel(entry.ownerKind)}</code>
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.ownerId}>
                    {entry.ownerId.slice(0, 8)}…
                  </td>
                  <td className="cp-program-data-cleanup-col-db">{entry.pageCount}</td>
                  <td className="cp-program-data-cleanup-col-db">{entry.pageIndexSummary}</td>
                  <td className="cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity" title={entry.contextLabel}>
                    {entry.contextLabel}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.learningSnippet}>
                    {entry.learningSnippet}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={presentationRefSlideLinkStateLabel(entry.linkState)}>
                    {presentationRefSlideLinkStateLabel(entry.linkState)}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.linkLabel}>
                    {entry.linkLabel}
                  </td>
                  <td className="cp-program-data-cleanup-col-db">{entry.lastUpdatedAt?.slice(0, 19) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="page-desc">{emptyMessage}</p>
      )}
    </section>
  );
};

export default PresentationAuditSection;
